import classnames from 'classnames';
import { PropertyValue } from 'csstype';
import Downshift, {
  ControllerStateAndHelpers,
  GetItemPropsOptions,
} from 'downshift';
import { GraphQLSchema } from 'graphql';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';

import { getSubjectSlugByGraphQLType } from 'src/app/graph/documentationPage/schemaPage/getSubjectSlugByGraphQLType';
import { FieldMatchDetails } from 'src/app/graph/documentationPage/schemaPage/SearchSchemaDoc/fieldMatchDetails/FieldMatchDetails';
import { SensiblyBreakCamelCase } from 'src/app/graph/explorerPage/components/sensiblyBreakCamelCase/SensiblyBreakCamelCase';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { FormattedTypeString } from 'src/components/common/formattedTypeString/FormattedTypeString';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { unwrapNestedType } from 'src/lib/graphql/graphqlHelpers';
import { GetSubgraphs } from 'src/lib/graphql/subgraphMetadataHelpers';

import {
  FieldWithParent,
  fieldSearchKindSorters,
  fieldSearchLengthSorters,
  searchSchemaForFields,
} from '../searchSchema/searchSchema';
import * as SpotlightLayoutComponents from '../spotlightLayoutComponents/SpotlightLayoutComponents';

// Padding to add to tht top and bottom of the virtualized list.
// See https://github.com/bvaughn/react-window#can-i-add-padding-to-the-top-and-bottom-of-a-list.
const LIST_PADDING_SIZE = 8;
const ITEM_HEIGHT = 39;

// Maximum number of items to store in search history
const MAX_SEARCH_HISTORY = 100;

interface ResultsRendererProps {
  data: {
    searchResults: ReturnType<typeof searchSchemaForFields>;
    getItemProps: (options: GetItemPropsOptions<FieldWithParent>) => unknown;
    highlightedIndex: number | null;
  };
  index: number;
  style: React.CSSProperties;
}
const ResultRenderer = ({
  data: { searchResults, getItemProps, highlightedIndex },
  index,
  style,
}: ResultsRendererProps) => {
  const item = searchResults[index];
  if (!item) throw new Error('item missing');

  const isHighlighted = highlightedIndex === index;
  const top: PropertyValue<typeof style.top> = style.top;
  const allStyles = {
    ...style,
    top: `${
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (typeof top === 'number' ? top : parseFloat(top!)) + LIST_PADDING_SIZE
    }px`,
  };
  const itemProps = getItemProps({
    style: allStyles,
    key: `${item.parentMatch.type.name}.${item.fieldMatch.field.name}`,
    index,
    item,
  });

  return (
    <li
      className={classnames(
        'flex cursor-pointer truncate rounded px-3 py-2 transition-colors duration-150',
        isHighlighted && 'bg-info',
      )}
      {...(typeof itemProps === 'object' ? itemProps : {})}
    >
      <span
        className={classnames(
          'inline-block min-w-0 truncate',
          isHighlighted ? 'text-info' : 'text-secondary',
        )}
        style={{ flexShrink: 3 }}
      >
        {item.parentMatch.matchedSegments?.map((segment, i, arr) => (
          <span
            key={arr
              .slice(0, i + 1)
              .map((s) => s.text)
              .join()}
            className={classnames(segment.matched && 'font-bold')}
          >
            {segment.text}
          </span>
        )) || item.parentMatch.type.name}
        .
      </span>
      <span
        className={classnames(
          'inline-block min-w-0 truncate',
          isHighlighted
            ? 'text-info'
            : item.fieldMatch.matchedSegments?.length
              ? 'text-primary'
              : 'text-secondary',
        )}
        style={{ flexShrink: 2 }}
      >
        {item.fieldMatch.matchedSegments?.map((segment, i, arr) => (
          <span
            key={arr
              .slice(0, i + 1)
              .map((s) => s.text)
              .join()}
            className={classnames(segment.matched && 'font-bold')}
          >
            {segment.text}
          </span>
        )) || item.fieldMatch.field.name}
      </span>
      {item.argumentMatch && (
        <span
          className={classnames(
            'inline-block min-w-0 truncate italic',
            isHighlighted ? 'text-info' : 'text-primary',
          )}
          style={{ flexShrink: 2 }}
        >
          (
          {item.argumentMatch?.matchedSegments?.map((segment, i, arr) => (
            <span
              key={arr
                .slice(0, i + 1)
                .map((s) => s.text)
                .join()}
              className={classnames(segment.matched && 'font-bold')}
            >
              {segment.text}
            </span>
          ))}
          )
        </span>
      )}
    </li>
  );
};

export const SpotlightSearch = ({
  graphqlSchema,
  onSelect,
  inputValue,
  onInputValueChange,
  initialHighlightedIndex,
  onHighlightedIndexChange,
  getSubgraphs,
}: {
  graphqlSchema: GraphQLSchema;
  inputValue: string;
  initialHighlightedIndex?: number | null | undefined;
  onHighlightedIndexChange?: (index: number | null | undefined) => void;
  onInputValueChange?: (
    inputValue: string,
    // Downshift's types have a bug where `ControllerStateAndHelpers` somehow
    // doesn't intersect with StateChangeOptions, so we have to override this
    // with in order to access the `type` field that exists here but whose
    // type isn't exposed.
    controllerStateAndHelpers: ControllerStateAndHelpers<FieldWithParent> & {
      type?: unknown;
    },
  ) => void;
  onSelect?: (
    selectedItem: FieldWithParent | null,
    initialValue: string,
  ) => void;
  getSubgraphs: GetSubgraphs | undefined;
}) => {
  const history = useHistory();
  const { locationToReference } = useInternalGraphLinking();

  const selectAndPushToHistory = (
    selectedItem: FieldWithParent | null,
    initialValue: string,
  ) => {
    if (selectedItem) {
      setSearchHistory(
        [
          ...searchHistory,
          {
            parent: selectedItem.parentMatch.type.name,
            field: selectedItem.fieldMatch.field.name,
          },
        ].slice(-MAX_SEARCH_HISTORY),
      );
    }
    onSelect?.(selectedItem, initialValue);
  };
  const [searchHistory, setSearchHistory] = useLocalStorage(
    'explorerSearchHistory',
  );
  const searchIndexForHistory = React.useMemo(
    () =>
      Object.fromEntries(
        searchHistory.map((item, i) => [`${item.parent}.${item.field}`, i + 1]),
      ),
    [searchHistory],
  );

  const searchResults = _.sortBy(
    searchSchemaForFields(graphqlSchema, inputValue),
    [
      ...fieldSearchKindSorters,
      (searchResult) =>
        -(
          searchIndexForHistory[
            `${searchResult.parentMatch.type.name}.${searchResult.fieldMatch.field.name}`
          ] ?? 0
        ),
      ...fieldSearchLengthSorters,
    ],
  );

  // Since the list is virtualized, highlighted elements out of the range wont
  // get scrolled into view automatically.
  const listRef = React.createRef<
    List & { state: { scrollOffset?: number } }
  >();
  const heightRef = React.useRef(0);

  // This is mostly copied from VariableSizeList, needed to account for the
  // padding we added.
  const scrollToItem = (highlightedIndex?: number | null) => {
    if (listRef.current && typeof highlightedIndex === 'number') {
      // Since we added padding to the list, scrollToItem doesn't work
      // properly. Manually scroll to a position.
      const currentScroll = listRef.current.state.scrollOffset ?? 0;

      const minOffset = currentScroll;
      const maxOffset = currentScroll + heightRef.current;

      const itemTop = highlightedIndex * ITEM_HEIGHT;
      const itemBottom = itemTop + ITEM_HEIGHT + LIST_PADDING_SIZE * 2;

      if (itemTop < minOffset) {
        listRef.current.scrollTo(itemTop);
      } else if (itemBottom > maxOffset) {
        listRef.current.scrollTo(itemBottom - heightRef.current);
      }
    }
  };

  return (
    <Downshift
      inputValue={inputValue}
      onInputValueChange={onInputValueChange}
      itemCount={searchResults.length}
      defaultHighlightedIndex={0}
      itemToString={(item) =>
        item
          ? `${item.parentMatch.type.name}.${item.fieldMatch.field.name}`
          : ''
      }
      onSelect={(selectedItem) =>
        selectAndPushToHistory(selectedItem, inputValue)
      }
      initialHighlightedIndex={initialHighlightedIndex}
      onStateChange={({ highlightedIndex }) => {
        onHighlightedIndexChange?.(highlightedIndex);
        scrollToItem(highlightedIndex);
      }}
      initialIsOpen={true}
    >
      {({
        getRootProps,
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        highlightedIndex,
      }) => {
        const highlightedSearchResult =
          highlightedIndex !== null && searchResults[highlightedIndex];
        return (
          <SpotlightLayoutComponents.Container {...getRootProps()}>
            <SpotlightLayoutComponents.SearchInput
              {...getInputProps({
                autoFocus: true,
              })}
              onBlur={(e) => {
                // prevent downshift from resetting the state on blur
                // https://github.com/downshift-js/downshift/issues/333#issuecomment-366398528
                e.preventDefault();
              }}
              spellCheck={false}
            />
            <SpotlightLayoutComponents.SearchIcon />
            {isOpen && (
              <div
                data-search-details
                className="flex"
                style={{ minHeight: '50vh', maxHeight: '70vh' }}
              >
                <SpotlightLayoutComponents.LeftPanelContainer>
                  {!searchResults.length && (
                    <div className="mx-3 mt-5 px-3 py-2 text-base italic text-secondary">
                      {getNoResultsMessage(inputValue)}
                    </div>
                  )}
                  <ul
                    {...getMenuProps()}
                    className="grow overflow-y-auto overflow-x-hidden px-3 font-mono text-base"
                  >
                    <AutoSizer>
                      {/* Height and width only undefined if disable props are passed in, so these 0s are not used */}
                      {({ height = 0, width = 0 }) => {
                        heightRef.current = height;
                        return (
                          <List
                            itemSize={ITEM_HEIGHT}
                            width={width}
                            height={height}
                            itemCount={searchResults.length}
                            itemData={{
                              searchResults,
                              getItemProps,
                              highlightedIndex,
                            }}
                            ref={listRef}
                            // Add padding to the list
                            // https://github.com/bvaughn/react-window#can-i-add-padding-to-the-top-and-bottom-of-a-list
                            innerElementType={({ style, ...rest }) => (
                              <div
                                style={{
                                  ...style,
                                  height: `${
                                    parseFloat(style.height) +
                                    LIST_PADDING_SIZE * 2
                                  }px`,
                                }}
                                {...rest}
                              />
                            )}
                          >
                            {ResultRenderer}
                          </List>
                        );
                      }}
                    </AutoSizer>
                  </ul>
                  <SpotlightLayoutComponents.InfoBar />
                </SpotlightLayoutComponents.LeftPanelContainer>
                <SpotlightLayoutComponents.RightPanelContainer>
                  {!highlightedSearchResult ? (
                    <SpotlightLayoutComponents.EmptyState>
                      Select a field from the results for more details
                    </SpotlightLayoutComponents.EmptyState>
                  ) : highlightedSearchResult.argumentMatch ? (
                    <div className="p-8">
                      <h2 className="text-xl font-semibold text-primary">
                        <SensiblyBreakCamelCase
                          camelCasedString={
                            highlightedSearchResult.argumentMatch.argument.name
                          }
                        />
                      </h2>
                      <h3 className="font-mono text-sm text-primary">
                        <FormattedTypeString
                          typeString={highlightedSearchResult.argumentMatch.argument.type.toString()}
                          typeNamePartClassName="text-info"
                        />
                      </h3>

                      <h4 className="mt-6 text-base font-semibold">
                        Description
                      </h4>
                      <p className="mt-2 text-sm text-secondary">
                        {highlightedSearchResult.argumentMatch.argument
                          .description || (
                          <span className="italic">
                            No description for this field.
                          </span>
                        )}
                      </p>
                    </div>
                  ) : (
                    <FieldMatchDetails
                      fieldMatch={highlightedSearchResult.fieldMatch}
                      parentType={highlightedSearchResult.parentMatch.type}
                      typeMap={graphqlSchema.getTypeMap()}
                      onSelect={(result) => {
                        if (!result) return;
                        let category, graphqlTypeName, linkedGraphqlEntity;
                        if (result.typeMatch) {
                          category = getSubjectSlugByGraphQLType(
                            result.typeMatch.type,
                          );
                          graphqlTypeName = unwrapNestedType(
                            result.typeMatch.type,
                          ).name;
                        }
                        if (result.fieldMatch) {
                          category = getSubjectSlugByGraphQLType(
                            result.fieldMatch.parentType,
                          );
                          graphqlTypeName = result.fieldMatch.parentType.name;
                          linkedGraphqlEntity = result.fieldMatch.field.name;
                        }
                        history.push(
                          locationToReference({
                            category,
                            graphqlTypeName,
                            linkedGraphqlEntity,
                          }),
                        );
                      }}
                      getSubgraphs={getSubgraphs}
                    />
                  )}
                </SpotlightLayoutComponents.RightPanelContainer>
              </div>
            )}
          </SpotlightLayoutComponents.Container>
        );
      }}
    </Downshift>
  );
};

const getNoResultsMessage = (input: string) => {
  const periodIndex = input.indexOf('.');
  if (periodIndex === -1) {
    return `There are no fields matching "${input}"`;
  }
  return `There are no fields matching "${input.slice(
    periodIndex + 1,
  )}" with a parent type matching "${input.slice(0, periodIndex)}"`;
};
